import {SessionEvent} from "../../../Engine/LiveSession/SessionEvent.js";
import {GetAnchorStatDayForTargetTime, GetStatDayContainingX, GetStatNightContainingX} from "../../../UI/Stats/Helpers.js";
import {JournalEntry, JournalSegment} from "../journalEntries/@JournalEntry.js";
import {Session} from "../sessions/@Session.js";

/**
 * A "session period" is a synthetic structure, meant to unify processing of day-time sessions and night-time sessions/journal-entries.
 * 
 * Main structure (for processing): SessionPeriod -> SleepCycle [cycleNumber:0 for daytime periods] -> EventGroups and JournalSegments
 * */
export class SessionPeriod {
	//static NewEmpty() { return new SessionPeriod({sessions: [], eventGroups: []}); }

	constructor(data: Partial<SessionPeriod>) { Object.assign(this, data); }

	startTime: number;
	endTime: number|n;
	/** Set to the start-time of the earliest thing that confirmed this session-period as being intended to include sleep. (eg. launch of night-session, or time of a dream-segment)
	 * If set, the sleep-period is considered a "night session-period"; else it's considered a "day session-period". 
	 * Also, this value is used for calculating the period's "day offset". (basic explanation: if set, anchor to stat-day based on this [interpreted as night event], rather than based on period's start-time [interpreted as day event]) */
	sleepStartTime: number|n;
	DayInfo(): {type: "day" | "night", anchorStatDay: number, statDay: number | null, statNight: number | null} {
		return this.sleepStartTime != null
			? {type: "night", anchorStatDay: GetAnchorStatDayForTargetTime(this.sleepStartTime, "night"), statDay: null, statNight: GetStatNightContainingX(this.sleepStartTime)}
			: {type: "day", anchorStatDay: GetAnchorStatDayForTargetTime(this.startTime, "day"), statDay: GetStatDayContainingX(this.startTime), statNight: null};
	}

	sessions: Session[];
	// concatened from sessions
	eventGroups: EventGroup[];

	journalEntries: JournalEntry[];
	// concatenated from journal entries
	journalSegments: JournalSegment[];

	// extra synthesized info
	sleepCycles: SleepCycle[];
}

export class SleepCycle {
	constructor(data: Omit<SleepCycle, "Type">) { Object.assign(this, data); }

	/**
	 * Starts at 1, for valid sleep cycles. 
	 * (Can be 0, which means this "sleep cycle" is just a container for a daytime/no-cycle session.)
	 */
	cycleNumber: number;
	Type() {
		return this.cycleNumber == 0 ? "day" : "night";
	}
	startTime: number;
	endTime: number|n;

	// basic logic for associating dream-segments and sleep-cycles: a given dream-segment is associated with the latest sleep-cycle whose end-time is before the dream-segment's end-time
	// (reason: regardless of whether the user or the engine adds the dream-segment, the sleep-period should have ended first to trigger the user/engine to add the dream-segment)
	// NOTE: This array does NOT include wake-segments. (those are excluded within DiscernSleepCyclesFromEventGroups())
	associatedDreamSegments: JournalSegment[];

	associatedSessions: Session[];

	// synthesized from associated-sessions
	// ==========

	associatedEventGroups: EventGroup[];

	alarmDelay: number; // in minutes (rounded)
	alarmWaitEndTime: number; // in unix-timestamp (ms)?
	responseTime: number; // in ms
	//cycleType: "sleep" | "wake"; //| "unknown";
	syntheticallyAddedSleepCycle?: boolean;
}

export type EventGroup = {
	sessionInfo: Session,
	type: "special" | "wait-period" | "cycle",
	events: SessionEvent[],
	//enderReached: boolean|n,
	duration: number, // in ms
}